<script setup>
import { onBeforeUnmount, ref } from 'vue';
import { cva } from 'class-variance-authority';

/**
 * A small panel anchored to its trigger; closes on Escape or a click outside.
 * The trigger slot gets { open, toggle }; the default slot gets { close }.
 */
const props = defineProps({
  placement: { type: String, default: 'bottom' },
  align: { type: String, default: 'end' },
  tone: { type: String, default: 'light' },
  label: { type: String, required: true },
});

const open = ref(false);
const root = ref(null);

const toggle = () => (open.value = !open.value);
const close = () => (open.value = false);

function onPointer(event) {
  if (open.value && root.value && !root.value.contains(event.target)) close();
}
window.addEventListener('pointerdown', onPointer, true);
onBeforeUnmount(() => window.removeEventListener('pointerdown', onPointer, true));

const panel = cva('absolute z-50 min-w-[200px] rounded-2xl p-2 shadow-[0_24px_60px_-16px_rgba(0,0,0,0.45)]', {
  variants: {
    placement: { top: 'bottom-full mb-2', bottom: 'top-full mt-2' },
    align: { start: 'left-0', end: 'right-0', center: 'left-1/2 -translate-x-1/2' },
    tone: { light: 'bg-white border border-deen-ink/10 text-deen-ink', dark: 'bg-[#0f2a2a] border border-white/10 text-white' },
  },
});
</script>

<template>
  <div ref="root" class="relative" @keydown.esc="close">
    <slot name="trigger" :open="open" :toggle="toggle" />
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 scale-95"
      leave-active-class="transition duration-100 ease-in"
      leave-to-class="opacity-0 scale-95"
    >
      <div v-if="open" role="dialog" :aria-label="label" :class="panel({ placement: props.placement, align: props.align, tone: props.tone })">
        <slot :close="close" />
      </div>
    </Transition>
  </div>
</template>
